import { useEffect, useCallback, useMemo, PropsWithChildren } from 'react';
import { FormProvider, UseFormReturn, Path } from 'react-hook-form';
import { HubDiscoveryValues } from '../../../../../../store/features/discovery';
import { conditionalDebounce } from '../../../../../../helpers';

export interface ProductFilterProviderProps<T> {
  form: UseFormReturn<Partial<T>>;
  onChangeFilter: (data: T, name?: Path<Partial<T>>) => void;
}

export function ProductFilterProvider<T extends Partial<HubDiscoveryValues>>(
  props: PropsWithChildren<ProductFilterProviderProps<T>>,
) {
  const { form, onChangeFilter, children } = props;
  const { watch } = form;

  // Create a memoized debounced version of onChangeFilter
  const debouncedOnChangeFilterFn = useMemo(
    () => conditionalDebounce(onChangeFilter, 100),
    [onChangeFilter],
  );

  // Use the memoized debounced function in useCallback
  const debouncedOnChangeFilter = useCallback(
    (data: T, name?: Path<Partial<T>>) => {
      debouncedOnChangeFilterFn(data, name);
    },
    [debouncedOnChangeFilterFn],
  );

  useEffect(() => {
    const subscription = watch((data, { name }) => {
      debouncedOnChangeFilter(data as unknown as T, name);
    });

    return () => {
      subscription.unsubscribe();
      if (typeof debouncedOnChangeFilterFn.cancel === 'function') {
        debouncedOnChangeFilterFn.cancel();
      }
    };
  }, [watch, debouncedOnChangeFilter, debouncedOnChangeFilterFn]);

  return <FormProvider {...form}>{children}</FormProvider>;
}
